import { useState, useEffect } from "react";
import ComponentCard from "../common/ComponentCard";
import Label from "../form/Label";
import Checkbox from "../form/input/Checkbox";
import Button from "../ui/button/Button";
import api from "../../services/api";
import { authService } from "../../services/authService";

interface EditCollaboratorFormProps {
    collaboratorId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

interface PermissionsState {
    full_access: boolean;
    dashboard: { view: boolean };
    sales: { view: boolean; abandoned: boolean; refund: boolean };
    subscriptions: { view: boolean; cancel: boolean };
    products: { view: boolean; edit: boolean };
    financial: { view: boolean; bank: boolean; withdrawal: boolean };
    integrations: { full: boolean };
    collaborators: { manage: boolean };
}

export default function EditCollaboratorForm({ collaboratorId, onSuccess, onError }: EditCollaboratorFormProps) {
    const [collaborator, setCollaborator] = useState<any>(null);
    const [permissions, setPermissions] = useState<PermissionsState>({
        full_access: false,
        dashboard: { view: false },
        sales: { view: false, abandoned: false, refund: false },
        subscriptions: { view: false, cancel: false },
        products: { view: false, edit: false },
        financial: { view: false, bank: false, withdrawal: false },
        integrations: { full: false },
        collaborators: { manage: false },
    });

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    useEffect(() => {
        if (collaboratorId) {
            fetchCollaborator();
        }
    }, [collaboratorId]);

    const fetchCollaborator = async () => {
        try {
            setLoadingData(true);
            const companyId = authService.getCompanyId();
            const response = await api.get(`/api/collaborators/${collaboratorId}?companyId=${companyId}`);

            // Backend responde no formato:
            // { status: 'success', data: { collaborator: { ... } } }
            const isSuccess = response.data?.success === true || response.data?.status === "success";

            if (isSuccess && response.data?.data?.collaborator) {
                const data = response.data.data.collaborator;
                setCollaborator(data);

                if (data.permissions) {
                    setPermissions(data.permissions);
                }
            } else {
                setCollaborator(null);
            }
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || "Erro ao carregar colaborador";
            setApiError(errorMsg);
            if (onError) {
                onError(error);
            }
        } finally {
            setLoadingData(false);
        }
    };

    const handleFullAccessChange = (checked: boolean) => {
        if (checked) {
            setPermissions({
                full_access: true,
                dashboard: { view: true },
                sales: { view: true, abandoned: true, refund: true },
                subscriptions: { view: true, cancel: true },
                products: { view: true, edit: true },
                financial: { view: true, bank: true, withdrawal: true },
                integrations: { full: true },
                collaborators: { manage: true },
            });
        } else {
            setPermissions({
                full_access: false,
                dashboard: { view: false },
                sales: { view: false, abandoned: false, refund: false },
                subscriptions: { view: false, cancel: false },
                products: { view: false, edit: false },
                financial: { view: false, bank: false, withdrawal: false },
                integrations: { full: false },
                collaborators: { manage: false },
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);
        setApiError("");

        try {
            const companyId = authService.getCompanyId();
            const response = await api.patch(`/api/collaborators/${collaboratorId}/permissions`, {
                companyId,
                permissions,
            });

            setApiSuccess("Permissões atualizadas com sucesso!");
            if (onSuccess) {
                setTimeout(() => onSuccess(response.data), 1500);
            }
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || "Erro ao atualizar permissões";
            setApiError(errorMsg);
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    if (loadingData) {
        return (
            <ComponentCard title="Editar Permissões">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
                </div>
            </ComponentCard>
        );
    }

    if (!collaborator) {
        return (
            <ComponentCard title="Editar Permissões">
                <div className="text-center py-12 text-gray-500">
                    Colaborador não encontrado
                </div>
            </ComponentCard>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6">
            <ComponentCard title="Editar Permissões">
                {apiError && (
                    <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                        {apiError}
                    </div>
                )}

                {apiSuccess && (
                    <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                        {apiSuccess}
                    </div>
                )}

                <div className="mb-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>Colaborador:</strong> {collaborator.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>E-mail:</strong> {collaborator.email}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mt-6">
                        <Label className="mb-3">Permissões</Label>

                        {/* Acesso total */}
                        <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <Checkbox
                                id="full_access"
                                checked={permissions.full_access}
                                onChange={handleFullAccessChange}
                                label="Acesso total ao sistema"
                            />
                        </div>

                        {!permissions.full_access && (
                            <div className="space-y-4">
                                {/* Dashboard */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <Checkbox
                                        id="dashboard_view"
                                        checked={permissions.dashboard.view}
                                        onChange={(checked) =>
                                            setPermissions({
                                                ...permissions,
                                                dashboard: { view: checked },
                                            })
                                        }
                                        label="Dashboard"
                                    />
                                </div>

                                {/* Vendas */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        Vendas
                                    </div>
                                    <div className="space-y-2 ml-4">
                                        <Checkbox
                                            id="sales_view"
                                            checked={permissions.sales.view}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    sales: { ...permissions.sales, view: checked },
                                                })
                                            }
                                            label="Visualizar vendas"
                                        />
                                        <Checkbox
                                            id="sales_abandoned"
                                            checked={permissions.sales.abandoned}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    sales: { ...permissions.sales, abandoned: checked },
                                                })
                                            }
                                            label="Vendas abandonadas"
                                        />
                                        <Checkbox
                                            id="sales_refund"
                                            checked={permissions.sales.refund}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    sales: { ...permissions.sales, refund: checked },
                                                })
                                            }
                                            label="Reembolsar vendas"
                                        />
                                    </div>
                                </div>

                                {/* Assinaturas */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        Assinaturas
                                    </div>
                                    <div className="space-y-2 ml-4">
                                        <Checkbox
                                            id="subscriptions_view"
                                            checked={permissions.subscriptions.view}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    subscriptions: { ...permissions.subscriptions, view: checked },
                                                })
                                            }
                                            label="Visualizar assinaturas"
                                        />
                                        <Checkbox
                                            id="subscriptions_cancel"
                                            checked={permissions.subscriptions.cancel}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    subscriptions: { ...permissions.subscriptions, cancel: checked },
                                                })
                                            }
                                            label="Cancelar assinaturas"
                                        />
                                    </div>
                                </div>

                                {/* Produtos */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        Produtos
                                    </div>
                                    <div className="space-y-2 ml-4">
                                        <Checkbox
                                            id="products_view"
                                            checked={permissions.products.view}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    products: { ...permissions.products, view: checked },
                                                })
                                            }
                                            label="Visualizar produtos"
                                        />
                                        <Checkbox
                                            id="products_edit"
                                            checked={permissions.products.edit}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    products: { ...permissions.products, edit: checked },
                                                })
                                            }
                                            label="Editar produtos"
                                        />
                                    </div>
                                </div>

                                {/* Financeiro */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        Financeiro
                                    </div>
                                    <div className="space-y-2 ml-4">
                                        <Checkbox
                                            id="financial_view"
                                            checked={permissions.financial.view}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    financial: { ...permissions.financial, view: checked },
                                                })
                                            }
                                            label="Visualizar financeiro"
                                        />
                                        <Checkbox
                                            id="financial_bank"
                                            checked={permissions.financial.bank}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    financial: { ...permissions.financial, bank: checked },
                                                })
                                            }
                                            label="Configurar dados bancários"
                                        />
                                        <Checkbox
                                            id="financial_withdrawal"
                                            checked={permissions.financial.withdrawal}
                                            onChange={(checked) =>
                                                setPermissions({
                                                    ...permissions,
                                                    financial: { ...permissions.financial, withdrawal: checked },
                                                })
                                            }
                                            label="Solicitar saque"
                                        />
                                    </div>
                                </div>

                                {/* Integrações */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <Checkbox
                                        id="integrations_full"
                                        checked={permissions.integrations.full}
                                        onChange={(checked) =>
                                            setPermissions({
                                                ...permissions,
                                                integrations: { full: checked },
                                            })
                                        }
                                        label="Integrações"
                                    />
                                </div>

                                {/* Colaboradores */}
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <Checkbox
                                        id="collaborators_manage"
                                        checked={permissions.collaborators.manage}
                                        onChange={(checked) =>
                                            setPermissions({
                                                ...permissions,
                                                collaborators: { manage: checked },
                                            })
                                        }
                                        label="Gerenciar colaboradores"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end gap-4">
                        <Button size="md" variant="outline" onClick={() => window.history.back()}>
                            Cancelar
                        </Button>
                        <Button size="md" variant="primary" onClick={() => handleSubmit({} as React.FormEvent)} disabled={loading}>
                            {loading ? "Salvando..." : "Salvar alterações"}
                        </Button>
                    </div>
                </form>
            </ComponentCard>
        </div>
    );
}
